import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '../../utils/analytics'
import { useDistanceUnit } from '../../hooks/useDistanceUnit'
import { baseInputClasses } from '../../styles/inputStyles'

/**
 * SearchLocationInput - Location search with radius selector
 * 
 * Features:
 * - Location text input with geocoding
 * - Radius dropdown selector
 * - Go button with loading state
 * - Current location button with loading state
 * - Spot Mode button (optional)
 * - Active state tracking for location method
 * - Responsive layout
 * - Comprehensive analytics tracking
 * 
 * @idempotent - Controlled component, state managed externally
 * @atomic - Single responsibility: location input controls
 */
const SearchLocationInput = memo(function SearchLocationInput({
  searchLocation,
  onSearchLocationChange,
  radius = null,
  onRadiusChange = null,
  onGeocode,
  onCurrentLocation,
  isGeocoding = false,
  isGettingLocation = false,
  isGeolocationAvailable = true,
  disabled = false,
  locationSource = null, // 'geocoded' | 'geolocation' | null
  className = ''
}) {
  const { t } = useTranslation()
  const { convertToKm, unit, isImperial } = useDistanceUnit()

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !disabled && !isGeocoding) {
      trackEvent('Search', 'location_input_enter', 'search_tab', null, {
        location: 'search_tab',
        has_value: searchLocation?.trim().length > 0,
        value_length: searchLocation?.length || 0
      })
      onGeocode()
    }
  }, [disabled, isGeocoding, onGeocode, searchLocation])

  const handleLocationInputFocus = useCallback(() => {
    trackEvent('Search', 'location_input_focus', 'search_tab', null, {
      location: 'search_tab',
      current_value: searchLocation || '',
      has_value: searchLocation?.trim().length > 0
    })
  }, [searchLocation])

  const handleLocationInputBlur = useCallback(() => {
    trackEvent('Search', 'location_input_blur', 'search_tab', null, {
      location: 'search_tab',
      final_value: searchLocation || '',
      has_value: searchLocation?.trim().length > 0,
      value_length: searchLocation?.length || 0
    })
  }, [searchLocation])

  const handleRadiusChange = useCallback((newRadius) => {
    if (!onRadiusChange) return
    trackEvent('Search', 'radius_change', 'search_tab', newRadius, {
      location: 'search_tab',
      old_radius: radius,
      new_radius: newRadius
    })
    onRadiusChange(newRadius)
  }, [radius, onRadiusChange])

  const handleGoClick = useCallback(() => {
    trackEvent('Search', 'go_button_click', 'search_tab', null, {
      location: 'search_tab',
      search_location: searchLocation || '',
      radius: radius,
      has_value: searchLocation?.trim().length > 0
    })
    onGeocode()
  }, [searchLocation, radius, onGeocode])

  const handleMyLocationClick = useCallback(() => {
    trackEvent('Search', 'my_location_click', 'search_tab', null, {
      location: 'search_tab',
      is_available: isGeolocationAvailable,
      current_source: locationSource
    })
    onCurrentLocation()
  }, [isGeolocationAvailable, locationSource, onCurrentLocation])

  // Imperial: clean round mile values stored as their km equivalents
  // Metric: standard km values — no dedup needed
  const displayRadiusOptions = isImperial
    ? [1, 5, 10, 25, 50].map(mi => ({
        kmValue: convertToKm(mi),
        displayValue: mi,
        label: `${mi} ${unit}`
      }))
    : [1, 5, 10, 25, 50].map(km => ({
        kmValue: km,
        displayValue: km,
        label: `${km} ${unit}`
      }))

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      {/* My Location icon button */}
      <button
        type="button"
        onClick={handleMyLocationClick}
        onTouchEnd={(e) => { e.preventDefault(); handleMyLocationClick() }}
        disabled={disabled || isGettingLocation || !isGeolocationAvailable}
        title={!isGeolocationAvailable ? t('map.geolocationNotSupported', 'Geolocation is not supported by your browser') : t('map.myLocationTitle', 'Use your current location')}
        aria-label={t('map.myLocationTitle', 'Use your current location')}
        className={`flex-shrink-0 p-2 rounded-md transition-colors ${
          locationSource === 'geolocation'
            ? 'text-primary bg-primary/10'
            : 'text-gray-500 hover:text-primary hover:bg-gray-100'
        } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {isGettingLocation ? (
          <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <svg className="pointer-events-none w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="3" />
            <line x1="12" y1="2" x2="12" y2="5" />
            <line x1="12" y1="19" x2="12" y2="22" />
            <line x1="2" y1="12" x2="5" y2="12" />
            <line x1="19" y1="12" x2="22" y2="12" />
          </svg>
        )}
      </button>

      {/* Location text input */}
      <input
        id="location-input"
        type="text"
        value={searchLocation}
        onChange={(e) => onSearchLocationChange(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={handleLocationInputFocus}
        onBlur={handleLocationInputBlur}
        placeholder={t('map.locationPlaceholder')}
        disabled={disabled}
        className={`${baseInputClasses} flex-1 min-w-0`}
      />

      {/* Radius dropdown — compact */}
      {onRadiusChange && (
        <select
          id="radius-select"
          value={radius}
          onChange={(e) => handleRadiusChange(parseInt(e.target.value))}
          disabled={disabled}
          className="w-16 px-1.5 py-2 text-xs bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-md focus:ring-2 focus:ring-primary focus:border-primary appearance-none disabled:bg-gray-100/80 disabled:cursor-not-allowed shadow-sm"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
            backgroundPosition: 'right 0.25rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1em 1em',
            paddingRight: '1.5rem'
          }}
        >
          {displayRadiusOptions.map(({ kmValue, label }) => (
            <option key={`radius-${kmValue}`} value={kmValue}>{label}</option>
          ))}
        </select>
      )}

      {/* Go button — icon only */}
      <button
        type="button"
        onClick={handleGoClick}
        onTouchEnd={(e) => { e.preventDefault(); handleGoClick() }}
        disabled={isGeocoding || !searchLocation?.trim() || disabled}
        title={t('map.go', 'Go')}
        aria-label={t('map.go', 'Go')}
        className={`flex-shrink-0 p-2 rounded-md transition-colors ${
          locationSource === 'geocoded'
            ? 'text-primary bg-primary/10'
            : 'text-gray-500 hover:text-primary hover:bg-gray-100'
        } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {isGeocoding ? (
          <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        )}
      </button>
    </div>
  )
})

export default SearchLocationInput
